import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Sparkles, Send, MessagesSquare, Eraser } from "lucide-react";
import { PageHeader, Spinner } from "../components/Brand";
import { useApp, API } from "../AppContext";

const newSession = () => `chat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function Assistant() {
  const { t, lang } = useApp();
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("progere_chat_session") || newSession());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { localStorage.setItem("progere_chat_session", sessionId); }, [sessionId]);

  useEffect(() => {
    axios.get(`${API}/ai/chat/${sessionId}`).then(({ data }) => setMessages(data || []));
  }, [sessionId]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" }); }, [messages, loading]);

  const send = async () => {
    if (!input.trim()) return;
    const msg = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg, id: `u-${Date.now()}` }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/ai/chat`, { session_id: sessionId, message: msg, language: lang });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, id: `a-${Date.now()}` }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Erreur: " + (e?.response?.data?.detail || e.message), id: `e-${Date.now()}` }]);
    } finally { setLoading(false); }
  };

  const clear = () => { setSessionId(newSession()); setMessages([]); };

  const suggestions = lang === "fr" ? [
    "Créer une soumission pour un nettoyage après construction de 32 000 pi²",
    "Rédige des exclusions pour des travaux de gypse et joints",
    "Calcule les heures de main-d'œuvre pour 5000 pi² de peinture",
    "Écris un courriel de suivi professionnel après 5 jours",
  ] : [
    "Create a quote for 32,000 sqft post-construction cleaning",
    "Write exclusions for drywall and joint finishing work",
    "Calculate labor hours for 5000 sqft of painting",
    "Write a professional follow-up email after 5 days",
  ];

  return (
    <div className="h-[calc(100vh-7rem)] flex flex-col">
      <PageHeader title={t.assistant_p.title}
        action={<button className="btn-ghost text-sm" onClick={clear} data-testid="chat-clear"><Eraser size={14} /> {t.assistant_p.clear}</button>} />

      <div ref={scrollRef} className="flex-1 glass-card p-6 overflow-y-auto mb-4">
        {messages.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 glow-gold" style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.3)" }}>
              <Sparkles size={28} color="#D4AF37" />
            </div>
            <p className="text-sm mb-6" style={{ color: "rgba(245,245,245,0.6)" }}>
              {lang === "fr" ? "Posez une question ou essayez une suggestion" : "Ask a question or try a suggestion"}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-3xl mx-auto">
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => setInput(s)} className="text-left p-3 rounded-2xl text-sm hover:border-[#D4AF37]/40 transition"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", color: "rgba(245,245,245,0.85)" }}
                  data-testid={`suggestion-${i}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="space-y-4 max-w-4xl mx-auto">
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className="max-w-2xl px-4 py-3 rounded-3xl text-sm whitespace-pre-wrap"
                style={m.role === "user"
                  ? { background: "linear-gradient(180deg, #C00000, #A00000)", color: "#F5F5F5" }
                  : { background: "rgba(212,175,55,0.06)", color: "rgba(245,245,245,0.92)", border: "1px solid rgba(212,175,55,0.15)" }}>
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="px-4 py-3 rounded-3xl text-sm flex items-center gap-2" style={{ background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.15)", color: "rgba(245,245,245,0.7)" }}>
                <Spinner /> {t.assistant_p.thinking}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="glass-card p-3 flex gap-2 items-end">
        <textarea
          rows={2}
          className="input-dark flex-1 resize-none"
          placeholder={t.assistant_p.placeholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          data-testid="chat-input"
        />
        <button className="btn-red" onClick={send} disabled={loading || !input.trim()} data-testid="chat-send">
          <Send size={16} /> {t.assistant_p.send}
        </button>
      </div>
      <p className="text-[11px] mt-2 text-center" style={{ color: "rgba(245,245,245,0.4)" }} data-testid="assistant-disclaimer">
        {lang === "fr"
          ? "Les résultats générés par l'intelligence artificielle doivent être vérifiés avant leur utilisation."
          : "AI-generated results must be reviewed before use."}
      </p>
    </div>
  );
}
